import React, { useEffect, useRef } from 'react';
import { drawFrameWithAngles } from '../../utils/canvasDrawing';
import type { PedalAnalysis, SimpleFrame } from '../../modules/ProcessingPipeline';
import type { FitEvidenceData } from './types';

interface Props {
  evidence: FitEvidenceData;
  frames: SimpleFrame[];
  bdc: PedalAnalysis | null;
  tdc: PedalAnalysis | null;
  activeView: 'bdc' | 'tdc';
  highlightId: string | null;
  onViewChange: (view: 'bdc' | 'tdc') => void;
}

/**
 * Which keypoint each marker hangs off. Pelvis travel anchors to the hip —
 * it is the hip's vertical movement that the number describes.
 */
const MARKER_KEYPOINT: Record<string, string> = {
  knee: 'rightKnee',
  hip: 'rightHip',
  trunk: 'rightShoulder',
  elbow: 'rightElbow',
  pelvis: 'rightHip',
};

/**
 * Convert a keypoint to a percentage of the frame.
 *
 * averagedKeypoints are PIXEL coordinates in the frame's own space, so they
 * must be normalized before they can position an HTML overlay. The design
 * mock's hardcoded percentages describe its static screenshots — do not
 * copy them.
 */
export function markerPosition(
  keypoints: Record<string, { x: number; y: number }> | undefined,
  markerId: string,
  width: number,
  height: number,
): { left: number; top: number } | null {
  const key = MARKER_KEYPOINT[markerId];
  if (!key || !keypoints || !width || !height) return null;
  const kp = keypoints[key];
  if (!kp) return null;
  return {
    left: Math.round((kp.x / width) * 10000) / 100,
    top: Math.round((kp.y / height) * 10000) / 100,
  };
}

const FitEvidence: React.FC<Props> = ({
  evidence, frames, bdc, tdc, activeView, highlightId, onViewChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const analysis = activeView === 'bdc' ? bdc : tdc;
  const frame = analysis ? frames[analysis.representativeFrameIndex] : undefined;
  const activeFrame = evidence.frames.find(f => f.view === activeView);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !analysis || !frame) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = frame.imageData.width;
    canvas.height = frame.imageData.height;

    const source = document.createElement('canvas');
    source.width = frame.imageData.width;
    source.height = frame.imageData.height;
    source.getContext('2d')?.putImageData(frame.imageData, 0, 0);

    drawFrameWithAngles(
      ctx,
      source.toDataURL('image/jpeg', 0.85),
      analysis.averagedKeypoints,
      analysis.angles.knee,
      analysis.angles.hip,
      analysis.angles.trunk,
      analysis.angles.elbow,
      undefined,
      undefined,
      // The values render as HTML markers below; without this they'd
      // appear twice.
      { skeletonOnly: true },
    );
  }, [analysis, frame]);

  const shotPanelId = 'fr-evidence-shot';
  const highlightedIsHere = activeFrame?.markers.some(m => m.id === highlightId) ?? false;

  return (
    <div className="fr-evidence">
      <div className="fr-vtabs" role="tablist">
        {evidence.frames.map(f => (
          <button
            key={f.view}
            id={`fr-evidence-tab-${f.view}`}
            type="button"
            role="tab"
            className="fr-vtab"
            aria-selected={f.view === activeView}
            aria-controls={shotPanelId}
            onClick={() => onViewChange(f.view)}
          >
            {f.label}
            <small>{f.sub}</small>
          </button>
        ))}
      </div>

      <div
        className="fr-shot"
        role="tabpanel"
        id={shotPanelId}
        aria-labelledby={`fr-evidence-tab-${activeView}`}
      >
        <canvas ref={canvasRef} aria-hidden="true" />
        {activeFrame?.markers.map(marker => {
          const pos = markerPosition(
            analysis?.averagedKeypoints,
            marker.id,
            frame?.imageData.width ?? 0,
            frame?.imageData.height ?? 0,
          );
          if (!pos) return null;
          const hot = highlightId === marker.id;
          const dim = highlightedIsHere && !hot;
          return (
            <div
              key={marker.id}
              className={
                `fr-marker fr-marker--${marker.status}` +
                (marker.kind === 'travel' ? ' fr-marker--travel' : '') +
                (hot ? ' fr-marker--hot' : '') +
                (dim ? ' fr-marker--dim' : '')
              }
              style={{ left: `${pos.left}%`, top: `${pos.top}%` }}
            >
              <span className="fr-marker__pt" />
              <span className="fr-marker__label">
                <span className="fr-marker__name">{marker.name}</span>
                <span className="fr-marker__value">{marker.value}</span>
              </span>
            </div>
          );
        })}
      </div>

      <div className="fr-shotfoot">{evidence.footnote}</div>
    </div>
  );
};

export default FitEvidence;
